import React from 'react';
import { ClipboardDocumentIcon, HeartIcon, EyeIcon, UserIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { Prompt } from '../../types';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { calculatePromptCompleteness } from '../../utils/promptPersonalization';
import Card from '../common/Card';
import Badge from '../common/Badge';
import Button from '../common/Button';

interface PromptCardProps {
  prompt: Prompt;
  onSelect: (prompt: Prompt) => void;
  onCopy: (content: string) => void;
  onToggleFavorite?: (promptId: number) => void;
  isFavorite?: boolean;
  compact?: boolean;
}

const PromptCard: React.FC<PromptCardProps> = ({
  prompt,
  onSelect,
  onCopy,
  onToggleFavorite,
  isFavorite = false,
  compact = false,
}) => {
  const { profile } = useUserProfile();
  const completeness = calculatePromptCompleteness(prompt.content, profile);
  
  const handleCopy = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    onCopy(prompt.content);
  };

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite?.(prompt.id);
  };

  const truncateContent = (content: string, maxLength: number = 120) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <Card
      hover
      padding={compact ? 'sm' : 'md'}
      className="cursor-pointer h-full min-h-[280px]"
      onClick={() => onSelect(prompt)}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className={`font-semibold text-gray-900 ${compact ? 'text-sm' : 'text-base'} line-clamp-2`}>
              {prompt.title}
            </h3>
            {!compact && (
              <p className="text-sm text-gray-600 mt-1">
                {prompt.chapterTitle}
              </p>
            )}
            
            {/* 개인화 완성도 표시 */}
            {completeness < 100 && (
              <div className="flex items-center gap-1 mt-1">
                <UserIcon className="w-3 h-3 text-blue-500" />
                <span className="text-xs text-blue-600">
                  {completeness}% 개인화
                </span>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-1 ml-2">
            {onToggleFavorite && (
              <button
                onClick={handleToggleFavorite}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
              >
                {isFavorite ? (
                  <HeartSolidIcon className="w-5 h-5 text-red-500" />
                ) : (
                  <HeartIcon className="w-5 h-5" />
                )}
              </button>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="p-1"
            >
              <ClipboardDocumentIcon className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content Preview */}
        {!compact && (
          <div className="flex-1 mb-3">
            <p className="text-sm text-gray-700 line-clamp-3">
              {truncateContent(prompt.content)}
            </p>
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {prompt.tags.slice(0, compact ? 2 : 3).map((tag, index) => (
            <Badge key={index} variant="primary" size="sm">
              {tag}
            </Badge>
          ))}
          {prompt.tags.length > (compact ? 2 : 3) && (
            <Badge variant="secondary" size="sm">
              +{prompt.tags.length - (compact ? 2 : 3)}
            </Badge>
          )}
        </div>

        {/* Variables */}
        {prompt.variables.length > 0 && !compact && (
          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-1">필요한 변수:</p>
            <div className="flex flex-wrap gap-1">
              {prompt.variables.slice(0, 2).map((variable, index) => (
                <Badge key={index} variant="secondary" size="sm">
                  [{variable}]
                </Badge>
              ))}
              {prompt.variables.length > 2 && (
                <Badge variant="secondary" size="sm">
                  +{prompt.variables.length - 2}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-3">
            <span className="flex items-center">
              <EyeIcon className="w-4 h-4 mr-1" />
              {prompt.usage_count || 0}
            </span>
            {prompt.is_premium && (
              <Badge variant="warning" size="sm">
                PRO
              </Badge>
            )}
          </div>
          
          <span>
            Chapter {prompt.chapterId}
          </span>
        </div>
      </div>
    </Card>
  );
};

export default PromptCard;